import { forwardRef } from 'react';

import { SECURITY_LEVEL_TO_BADGE_VARIANT } from '../../hooks/useSecurityAssessment';
import { fontStyles } from '../../styles/typography';
import { LoadingSpinner } from '../../ui/Icons/Animated/LoadingSpinner';
import {
  BadgeIcon,
  BadgeVariant,
} from '../TokenProfile/TokenProfileSecurityAudit/RiskIcons/BadgeIcon';
import { SecurityLevel } from '../TokenProfile/TokenProfileSecurityAudit/utils';

type Level = SecurityLevel | 'Unknown';

type Variant = 'default' | 'short';

type Props = {
  level: Level;
  numberOfRisks: number;
  variant?: Variant;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
};

function getLabel(level: Level, variant: Variant, numberOfRisks: number) {
  if (variant === 'default') {
    return ['Pending', 'Unknown', 'Danger'].includes(level) ? level : numberOfRisks;
  }

  if (variant === 'short') {
    if (level === 'Pending') {
      return (
        <LoadingSpinner className="ml-0 border-t-token-gray-1600 border-x-token-alphalight-400 border-b-token-alphalight-400" />
      );
    }
    if (level === 'Danger') {
      return 'Danger';
    }
    if (level === 'Unknown') {
      return '-';
    }
    return numberOfRisks;
  }
}

const levelToBadgeVariant: Record<Level, BadgeVariant> = {
  ...SECURITY_LEVEL_TO_BADGE_VARIANT,
  Unknown: 'pending',
};

export const SecurityScoreBadge = forwardRef<HTMLDivElement, Props>(function SecurityScoreBadge(
  { level, numberOfRisks, variant = 'default', className, onClick }: Props,
  forwardedRef,
) {
  return (
    <div
      ref={forwardedRef}
      onClick={onClick}
      className={fontStyles({
        variant: 'LABEL_SM',
        class: [
          'items-center',
          'rounded-2xl',
          'flex',
          'justify-center',
          'h-8',
          'px-4',
          'gap-1.5',
          'min-w-16',
          'w-fit',
          'outline-none',
          'bg-token-alphalight-300',
          'hover:bg-token-alphalight-400',
          'active:bg-token-alphalight-400',
          'focus-visible:outline-token-alphalight-500',
          'focus-visible:outline-2',
          'focus-visible:outline-solid',
          className,
        ],
      })}
    >
      <BadgeIcon variant={levelToBadgeVariant[level]} />
      {getLabel(level, variant, numberOfRisks)}
    </div>
  );
});
